import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

import type {
  CheckoutLineItem,
  MailingAddress,
  ProductVariant as ShopifyProductVariant,
} from "@redotech/redo-common-model/common/shopify-pixel-types";
import {
  CoverageFeeProductMatchType,
  isCoverageFeeProduct,
  NO_COVERAGE_PRODUCT_IDS,
} from "@redotech/redo-return-coverage-common/is-coverage-fee-product";
import { RobustCheckout } from "@redotech/redo-shopify-checkout-schema-common/shopify-checkout";
import {
  parseCheckoutPrice,
  ShopifyCheckoutWebhook,
  CheckoutLineItem as WebhookCheckoutLineItem,
} from "@redotech/redo-shopify-common/shopify-checkout-webhook";
import { getFullName } from "@redotech/string/name";

////////////////////////////
/////// Redo types /////////
////////////////////////////

const redoStorefrontEventMoneySchema_ = z.object({
  amount: zExt.coerceNull(z.number()),
  currencyCode: zExt.coerceNull(z.string()),
});
export const redoStorefrontEventMoneySchema: z.ZodType<RedoStorefrontEventMoney> =
  redoStorefrontEventMoneySchema_;
export interface RedoStorefrontEventMoney extends z.infer<
  typeof redoStorefrontEventMoneySchema_
> {}

export enum RedoDiscountApplicationType {
  AUTOMATIC = "AUTOMATIC",
  DISCOUNT_CODE = "DISCOUNT_CODE",
  MANUAL = "MANUAL",
  SCRIPT = "SCRIPT",
}

export enum RedoDiscountApplicationAllocationMethod {
  ACROSS = "ACROSS",
  EACH = "EACH",
}

export enum RedoDiscountApplicationTargetSelection {
  ALL = "ALL",
  ENTITLED = "ENTITLED",
  EXPLICIT = "EXPLICIT",
}

export enum RedoDiscountApplicationTargetType {
  LINE_ITEM = "LINE_ITEM",
  SHIPPING_LINE = "SHIPPING_LINE",
}

const redoDiscountApplicationSchema_ = z.object({
  title: z.string(), // The discount code or promotion name
  type: z.enum(RedoDiscountApplicationType),
  allocationMethod: z.enum(RedoDiscountApplicationAllocationMethod),
  targetSelection: z.enum(RedoDiscountApplicationTargetSelection),
  targetType: z.enum(RedoDiscountApplicationTargetType),
  value: z.union([
    z.object({
      type: z.literal("money"),
      amount: z.number(),
      currencyCode: z.string(),
    }),
    z.object({ type: z.literal("percentage"), percentage: z.number() }),
  ]),
});
export const redoDiscountApplicationSchema: z.ZodType<RedoDiscountApplication> =
  redoDiscountApplicationSchema_;

export interface RedoDiscountApplication extends z.infer<
  typeof redoDiscountApplicationSchema_
> {}

/**
 * Discount allocation as reported by the Shopify checkout UI extension
 * (`useDiscountAllocations`). Carries Shopify's already-applied
 * `discountedAmount` (no reconstruction needed) but lacks
 * `targetType` / `targetSelection` / `allocationMethod` / `value`.
 *
 * Only populated for Shopify Plus merchants (the extension runs on Plus).
 * When present on `RedoCheckoutInfo`, the Recover mirror-cart discount check
 * applies these codes to the mirror cart alongside `discountApplications`.
 */
const shopifyCartDiscountAllocationSchema_ = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("code"),
    code: z.string(),
    discountedAmount: redoStorefrontEventMoneySchema_,
  }),
  z.object({
    type: z.literal("automatic"),
    title: z.string(),
    discountedAmount: redoStorefrontEventMoneySchema_,
  }),
  z.object({
    type: z.literal("custom"),
    title: z.string(),
    discountedAmount: redoStorefrontEventMoneySchema_,
  }),
]);
export const shopifyCartDiscountAllocationSchema: z.ZodType<ShopifyCartDiscountAllocation> =
  shopifyCartDiscountAllocationSchema_;
export type ShopifyCartDiscountAllocation = z.infer<
  typeof shopifyCartDiscountAllocationSchema_
>;

/**
 * Build `RedoDiscountApplication[]` from extension-reported allocations.
 *
 * Allocations only carry `type`, `code`/`title`, and `discountedAmount` —
 * they do not carry `targetType`, `targetSelection`, `allocationMethod`, or
 * the discount definition's `value`. We fill those with benign defaults
 * because the only consumer of the reconstructed list is the advanced-flow
 * condition evaluator, and its discount predicates read only `.title` (for
 * `discount_code`) or nothing at all (for `discount_percentage`, which is
 * driven by cart totals).
 *
 * Use when you need the codes/titles of what Shopify actually applied to the
 * cart — this is ground truth from the checkout UI extension, strictly more
 * accurate than the webhook/pixel reconstruction for Plus merchants.
 */
export function allocationsToDiscountApplications(
  allocations: ShopifyCartDiscountAllocation[],
): RedoDiscountApplication[] {
  return allocations.map((allocation): RedoDiscountApplication => {
    const title =
      allocation.type === "code" ? allocation.code : allocation.title;
    const type =
      allocation.type === "code"
        ? RedoDiscountApplicationType.DISCOUNT_CODE
        : RedoDiscountApplicationType.AUTOMATIC;
    return {
      title,
      type,
      // Benign defaults — condition-eval predicates never read these.
      allocationMethod: RedoDiscountApplicationAllocationMethod.ACROSS,
      targetSelection: RedoDiscountApplicationTargetSelection.ALL,
      targetType: RedoDiscountApplicationTargetType.LINE_ITEM,
      // `RedoDiscountApplication.value` requires non-null money fields, but
      // the pixel-event money schema is nullable; condition-eval doesn't read
      // `.value`, so the fallback values are cosmetic.
      value: {
        type: "money",
        amount: allocation.discountedAmount.amount ?? 0,
        currencyCode: allocation.discountedAmount.currencyCode ?? "",
      },
    };
  });
}

const redoStorefrontEventProductSchema_ = z.object({
  parentProduct: zExt.coerceNull(
    z.object({
      productId: zExt.coerceNull(z.coerce.string()),
      title: zExt.coerceNull(z.string()),
      untranslatedTitle: zExt.coerceNull(z.string()),
      url: zExt.coerceNull(z.string()),
    }),
  ),
  isRedoProduct: z
    .boolean()
    .nullish()
    .transform((val) => val ?? false),
  variantId: zExt.coerceNull(z.coerce.string()),
  title: z.string().nullish(),
  untranslatedTitle: z.string().nullish(),
  sku: zExt.coerceNull(z.string()),
  imageUrl: zExt.coerceNull(z.string()),
  price: zExt.coerceNull(redoStorefrontEventMoneySchema_),
});
export const redoStorefrontEventProductSchema: z.ZodType<RedoStorefrontEventProduct> =
  redoStorefrontEventProductSchema_;
export interface RedoStorefrontEventProduct extends z.infer<
  typeof redoStorefrontEventProductSchema_
> {}

const redoCartLineSchema_ = z.object({
  ...redoStorefrontEventProductSchema_.shape,
  quantity: zExt.coerceNull(z.number()),
});
export const redoCartLineSchema: z.ZodType<RedoStorefrontEventCartLine> =
  redoCartLineSchema_;
export interface RedoStorefrontEventCartLine extends z.infer<
  typeof redoCartLineSchema_
> {}

/** Like ExtendedAddress, but all fields are nullable, and
 * missing fields are converted to null when parsing */
const redoCheckoutAddressSchema = z.object({
  street1: zExt.coerceNull(z.string()),
  street2: zExt.coerceNull(z.string()),
  city: zExt.coerceNull(z.string()),
  state: zExt.coerceNull(z.string()),
  postalCode: zExt.coerceNull(z.string()),
  country: zExt.coerceNull(z.string()),

  name: zExt.coerceNull(z.string()),
  firstName: zExt.coerceNull(z.string().optional()),
  lastName: zExt.coerceNull(z.string().optional()),

  phone: zExt.coerceNull(z.string().optional()),
  email: zExt.coerceNull(z.string().optional()),
  company: zExt.coerceNull(z.string().optional()),
});
type RedoCheckoutAddress = z.infer<typeof redoCheckoutAddressSchema>;

const redoCheckoutInfoSchema_ = z.object({
  cartId: zExt.coerceNull(z.string()),
  cartLines: zExt.coerceNull(z.array(redoCartLineSchema_)),
  shippingAddress: zExt.coerceNull(redoCheckoutAddressSchema),
  billingAddress: zExt.coerceNull(redoCheckoutAddressSchema),

  subtotal: zExt.coerceNull(redoStorefrontEventMoneySchema_),
  tax: zExt.coerceNull(redoStorefrontEventMoneySchema_),
  shipping: zExt.coerceNull(redoStorefrontEventMoneySchema_),
  totalPrice: zExt.coerceNull(redoStorefrontEventMoneySchema_),

  // Pre-discount subtotal for providers that report it (BigCommerce); feeds
  // discount-percentage. Shopify omits it and reconstructs from line prices.
  subtotalBeforeDiscounts: redoStorefrontEventMoneySchema_.optional(),

  // Discount information from Shopify
  discountApplications: zExt.coerceNull(
    z.array(redoDiscountApplicationSchema_),
  ),

  checkoutNoteAttributeTokens: z.array(z.string()).nullish(),
  checkoutLineItemPropertyTokens: z.array(z.string()).nullish(),

  // Shopify Plus only: discount allocations reported live by the checkout UI
  // extension (`useDiscountAllocations`). `undefined` = data not collected
  // (non-Plus merchant or extension didn't fire); an array (even empty) =
  // collected, and downstream takes the extension path.
  shopifyCartDiscountAllocations: z
    .array(shopifyCartDiscountAllocationSchema_)
    .optional(),

  hasInferredBundle: z.boolean().optional(),
});
export const redoCheckoutInfoSchema: z.ZodType<RedoCheckoutInfo> =
  redoCheckoutInfoSchema_;
export interface RedoCheckoutInfo extends z.infer<
  typeof redoCheckoutInfoSchema_
> {}

///////////////////////////
///// Shopify types ///////
///////////////////////////

export function shopifyProductVariantToRedoProductVariant(
  productVariant: ShopifyProductVariant,
  redoCoverageProductIds: ReadonlySet<string> = NO_COVERAGE_PRODUCT_IDS,
): z.infer<typeof redoStorefrontEventProductSchema> {
  return {
    parentProduct: {
      productId: productVariant.product.id,
      title: productVariant.product.title,
      untranslatedTitle: productVariant.product.untranslatedTitle,
      url: productVariant.product.url,
    },
    isRedoProduct: isCoverageFeeProduct({
      product: {
        vendor: productVariant.product.vendor,
        sku: productVariant.sku,
        product_id: productVariant.product.id,
      },
      redoCoverageProductIds,
      matchType: CoverageFeeProductMatchType.REDO_ONLY,
    }),
    variantId: productVariant.id,
    title: productVariant.title,
    untranslatedTitle: productVariant.untranslatedTitle,
    sku: productVariant.sku,
    imageUrl: productVariant.image?.src ?? null,
    price: {
      amount: productVariant.price.amount,
      currencyCode: productVariant.price.currencyCode,
    },
  };
}

/**
 * @see _ https://shopify.dev/docs/api/web-pixels-api/standard-events/product_added_to_cart
 * @see _ https://community.shopify.com/t/we-are-receiving-null-and-undefined-as-the-values-for-fields-from-pixel-event/363753
 *
 * Sometimes most fields are null, undefined, or missing.
 */
export const shopifyCartLineSchema_ = z.object({
  cost: z.object({
    totalAmount: z.object({
      amount: z.number().nullable(),
      currencyCode: z.string().nullish(),
    }),
  }),
  quantity: z.number().nullish(),
  merchandise: z.object({
    id: z.string().nullish(), // yes, actually nullable
    title: z.string().nullish(),
    product: z.object({
      id: z.string().nullish(), // yes, actually nullable
      title: z.string().nullish(),
      type: z.string().nullish(),
      untranslatedTitle: z.string().nullish(),
      url: z.string().nullish(),
      vendor: z.string().nullish(),
    }),
    image: z.object({ src: z.string().nullish() }).nullish(),
    price: z.object({
      amount: z.number().optional(),
      currencyCode: z.string().nullish(),
    }),
    sku: z.string().nullish(),
    untranslatedTitle: z.string().nullish(),
  }),
});
export interface ShopifyCartLine extends z.infer<
  typeof shopifyCartLineSchema_
> {}
export const shopifyCartLineSchema: z.ZodType<ShopifyCartLine> =
  shopifyCartLineSchema_;

/**
 * Shopify claims to potentially give us all of this information, but in practice
 * it's just the cart ID, so we have to fill in the rest by fetching the cart again.
 *
 * However, since Shopify doesn't make any guarantees about what they send us,
 * let's keep the full schema here in case that ever changes.
 */
const shopifyPixelEventCartSchema_ = z.object({
  id: z.string().nullish(),
  cost: z
    .object({
      totalAmount: z.object({
        amount: z.number(),
        currencyCode: z.string().nullish(),
      }),
    })
    .nullish(),
  totalQuantity: z.number().nullish(),
  lines: z.array(shopifyCartLineSchema).nullish(),
});
export interface ShopifyPixelEventCart extends z.infer<
  typeof shopifyPixelEventCartSchema_
> {}
export const shopifyPixelEventCartSchema: z.ZodType<ShopifyPixelEventCart> =
  shopifyPixelEventCartSchema_;

export function shopifyCartLineToRedoProductVariant(
  cartLine: z.infer<typeof shopifyCartLineSchema> | null | undefined,
  redoCoverageProductIds: ReadonlySet<string> = NO_COVERAGE_PRODUCT_IDS,
): z.infer<typeof redoStorefrontEventProductSchema> {
  return {
    parentProduct: {
      productId: cartLine?.merchandise.product.id ?? null,
      title: cartLine?.merchandise.product.title ?? null,
      untranslatedTitle:
        cartLine?.merchandise.product.untranslatedTitle ?? null,
      url: cartLine?.merchandise.product.url ?? null,
    },
    isRedoProduct: isCoverageFeeProduct({
      product: {
        vendor: cartLine?.merchandise.product.vendor,
        sku: cartLine?.merchandise.sku,
        product_id: cartLine?.merchandise.product.id,
      },
      redoCoverageProductIds,
      matchType: CoverageFeeProductMatchType.REDO_ONLY,
    }),
    variantId: cartLine?.merchandise.id ?? null,
    title: cartLine?.merchandise.title ?? null,
    untranslatedTitle: cartLine?.merchandise.untranslatedTitle ?? null,
    sku: cartLine?.merchandise.sku ?? null,
    imageUrl: cartLine?.merchandise.image?.src ?? null,
    price: {
      amount: cartLine?.merchandise.price.amount ?? null,
      currencyCode: cartLine?.merchandise.price.currencyCode ?? null,
    },
  };
}
export function shopifyCartLineToRedoCartLine(
  cartLine: z.infer<typeof shopifyCartLineSchema> | null | undefined,
  redoCoverageProductIds: ReadonlySet<string> = NO_COVERAGE_PRODUCT_IDS,
): z.infer<typeof redoCartLineSchema> {
  return {
    ...shopifyCartLineToRedoProductVariant(cartLine, redoCoverageProductIds),
    quantity: cartLine?.quantity ?? null,
  };
}
export function shopifyCheckoutLineItemToRedoCartLine(
  checkoutLineItem: CheckoutLineItem,
  redoCoverageProductIds: ReadonlySet<string> = NO_COVERAGE_PRODUCT_IDS,
): z.infer<typeof redoCartLineSchema> {
  return {
    parentProduct: {
      productId: checkoutLineItem?.variant?.product.id ?? null,
      title: checkoutLineItem?.variant?.product.title ?? null,
      untranslatedTitle:
        checkoutLineItem?.variant?.product.untranslatedTitle ?? null,
      url: checkoutLineItem?.variant?.product.url ?? null,
    },
    isRedoProduct: isCoverageFeeProduct({
      product: {
        vendor: checkoutLineItem?.variant?.product.vendor,
        sku: checkoutLineItem?.variant?.sku,
        product_id: checkoutLineItem?.variant?.product.id,
      },
      redoCoverageProductIds,
      matchType: CoverageFeeProductMatchType.REDO_ONLY,
    }),
    variantId: checkoutLineItem?.variant?.id ?? null,
    title: checkoutLineItem?.variant?.title ?? null,
    untranslatedTitle: checkoutLineItem?.variant?.untranslatedTitle ?? null,
    sku: checkoutLineItem?.variant?.sku ?? null,
    imageUrl: checkoutLineItem?.variant?.image?.src ?? null,
    price: {
      amount: checkoutLineItem?.variant?.price.amount ?? null,
      currencyCode: checkoutLineItem?.variant?.price.currencyCode ?? null,
    },
    quantity: checkoutLineItem.quantity,
  };
}

export function shopifyWebhookLineItemToRedoCartLine(
  lineItem: WebhookCheckoutLineItem,
  currency: string | null | undefined,
  redoCoverageProductIds: ReadonlySet<string> = NO_COVERAGE_PRODUCT_IDS,
): z.infer<typeof redoCartLineSchema> {
  return {
    parentProduct: {
      productId: lineItem.product_id?.toString() ?? null,
      title: lineItem.presentment_title ?? lineItem.title ?? null,
      untranslatedTitle: lineItem.title ?? lineItem.presentment_title ?? null,
      url: null,
    },
    isRedoProduct: isCoverageFeeProduct({
      product: {
        vendor: lineItem.vendor,
        sku: lineItem.sku,
        product_id: lineItem.product_id?.toString(),
      },
      redoCoverageProductIds,
      matchType: CoverageFeeProductMatchType.REDO_ONLY,
    }),
    variantId: lineItem.variant_id?.toString() ?? null,
    title:
      lineItem.presentment_variant_title ??
      lineItem.variant_title ??
      lineItem.presentment_title ??
      lineItem.title ??
      null,
    untranslatedTitle:
      lineItem.variant_title ??
      lineItem.presentment_variant_title ??
      lineItem.title ??
      lineItem.presentment_title ??
      null,
    sku: lineItem.sku ?? null,
    imageUrl: null,
    price: {
      amount: parseCheckoutPrice(lineItem.price),
      currencyCode: currency ?? null,
    },
    quantity: lineItem.quantity ?? null,
  };
}

function shopifyMailingAddressToRedoAddress(
  mailingAddress: MailingAddress,
): RedoCheckoutAddress {
  return {
    street1: mailingAddress.address1,
    street2: mailingAddress.address2,
    city: mailingAddress.city,
    state: mailingAddress.province,
    country: mailingAddress.country,
    postalCode: mailingAddress.zip,

    name:
      getFullName(mailingAddress.firstName, mailingAddress.lastName) || null,
    firstName: mailingAddress.firstName,
    lastName: mailingAddress.lastName,

    phone: mailingAddress.phone,
    email: null,
    company: null,
  };
}

function shopifyWebhookAddressToRedoAddress(
  address: NonNullable<ShopifyCheckoutWebhook["shipping_address"]>,
): RedoCheckoutAddress {
  return {
    street1: address.address1 ?? null,
    street2: address.address2 ?? null,
    city: address.city ?? null,
    state: address.province ?? null,
    postalCode: address.zip ?? null,
    country: address.country ?? null,
    name:
      [address.first_name, address.last_name].filter(Boolean).join(" ") || null,
    firstName: address.first_name ?? null,
    lastName: address.last_name ?? null,
    phone: address.phone ?? null,
    email: null,
    company: address.company ?? null,
  };
}

/**
 * Transform Shopify discount application to Redo format
 */
function shopifyDiscountApplicationToRedoDiscount(
  discount: any,
): RedoDiscountApplication {
  // Determine if value is money or percentage
  const value =
    "amount" in discount.value && "currencyCode" in discount.value
      ? {
          type: "money" as const,
          amount: discount.value.amount,
          currencyCode: discount.value.currencyCode,
        }
      : { type: "percentage" as const, percentage: discount.value.percentage };

  return {
    title: discount.title,
    type: discount.type,
    allocationMethod: discount.allocationMethod,
    targetSelection: discount.targetSelection,
    targetType: discount.targetType,
    value,
  };
}

/**
 * Transform webhook discount_codes to Redo format.
 * Webhook discount codes have less information than pixel discountApplications,
 * so we infer reasonable defaults for missing fields.
 *
 * Note: Webhook always provides the dollar amount, even for percentage discounts,
 * so we always use the money format for the value.
 */
function shopifyWebhookDiscountCodesToRedoDiscounts(
  discountCodes: ShopifyCheckoutWebhook["discount_codes"],
  currencyCode: string | null | undefined,
): RedoDiscountApplication[] | null {
  if (!discountCodes || discountCodes.length === 0) {
    return null;
  }

  return discountCodes.map((discount) => {
    const amount = parseCheckoutPrice(discount.amount);

    // Determine target type based on discount type
    const targetType =
      discount.type === "shipping"
        ? RedoDiscountApplicationTargetType.SHIPPING_LINE
        : RedoDiscountApplicationTargetType.LINE_ITEM;

    // Webhook always provides the calculated dollar amount, even for percentage discounts
    const value = {
      type: "money" as const,
      amount,
      currencyCode: currencyCode ?? "USD",
    };

    return {
      title: discount.code ?? "",
      type: RedoDiscountApplicationType.DISCOUNT_CODE,
      allocationMethod: RedoDiscountApplicationAllocationMethod.ACROSS,
      targetSelection: RedoDiscountApplicationTargetSelection.ALL,
      targetType,
      value,
    };
  });
}

function isWebhookCheckout(
  checkout: RobustCheckout | ShopifyCheckoutWebhook | null | undefined,
): checkout is ShopifyCheckoutWebhook {
  if (!checkout) {
    return false;
  }
  if ("_type" in checkout && checkout._type === "webhook") {
    return true;
  }
  return false;
}

interface CheckoutMetadataEntry {
  key: string;
  value?: unknown;
}

export function buildCheckoutMetadataTokens(
  entries: CheckoutMetadataEntry[] | null | undefined,
): string[] {
  const tokens = new Set<string>();

  for (const entry of entries ?? []) {
    tokens.add(entry.key);

    const { value } = entry;
    if (
      value == null ||
      (typeof value !== "string" &&
        typeof value !== "number" &&
        typeof value !== "boolean")
    ) {
      continue;
    }

    tokens.add(`${entry.key}=${String(value)}`);
  }

  return [...tokens];
}

function pixelCheckoutAttributesToMetadataEntries(
  checkout: RobustCheckout,
): CheckoutMetadataEntry[] {
  return (checkout.attributes ?? []).map(({ key, value }) => ({ key, value }));
}

function pixelLineItemPropertiesToMetadataEntries(
  checkout: RobustCheckout,
): CheckoutMetadataEntry[] {
  return (checkout.lineItems ?? []).flatMap((lineItem) =>
    (lineItem.properties ?? []).map(({ key, value }) => ({ key, value })),
  );
}

function webhookNoteAttributesToMetadataEntries(
  checkout: ShopifyCheckoutWebhook,
): CheckoutMetadataEntry[] {
  return (checkout.note_attributes ?? []).map(({ name, value }) => ({
    key: name,
    value,
  }));
}

function webhookLineItemPropertiesToMetadataEntries(
  checkout: ShopifyCheckoutWebhook,
): CheckoutMetadataEntry[] {
  return (checkout.line_items ?? []).flatMap((lineItem) =>
    Object.entries(lineItem.properties ?? {}).map(([key, value]) => ({
      key,
      value,
    })),
  );
}

export function shopifyCheckoutToRedoCheckout({
  checkout,
  cartId,
  pixelCheckout,
  shopifyCartDiscountAllocations,
  redoCoverageProductIds,
}: {
  checkout: RobustCheckout | ShopifyCheckoutWebhook | null | undefined;
  cartId: string | null;
  /** Optional pixel checkout data. If provided alongside webhook checkout, pixel's discountApplications will be preferred. */
  pixelCheckout?: RobustCheckout | null;
  /**
   * Shopify Plus only: allocations reported live by the checkout UI
   * extension. When present (even as an empty array) downstream takes the
   * extension path instead of the legacy reconstruction.
   */
  shopifyCartDiscountAllocations?: ShopifyCartDiscountAllocation[];
  redoCoverageProductIds?: ReadonlySet<string>;
}): z.infer<typeof redoCheckoutInfoSchema> {
  if (!checkout) {
    return {
      cartId,
      cartLines: null,
      subtotal: null,
      tax: null,
      shipping: null,
      totalPrice: null,
      shippingAddress: null,
      billingAddress: null,
      discountApplications: null,
      checkoutNoteAttributeTokens: [],
      checkoutLineItemPropertyTokens: [],
      shopifyCartDiscountAllocations,
    };
  }

  if (isWebhookCheckout(checkout)) {
    const shippingLine = checkout.shipping_lines?.[0];
    return {
      cartId: cartId ?? checkout.cart_token ?? null,
      cartLines:
        checkout.line_items?.map((item) =>
          shopifyWebhookLineItemToRedoCartLine(
            item,
            checkout.presentment_currency ?? checkout.currency,
            redoCoverageProductIds,
          ),
        ) ?? null,
      subtotal: {
        amount: parseCheckoutPrice(checkout.subtotal_price),
        currencyCode:
          checkout.presentment_currency ?? checkout.currency ?? null,
      },
      tax: {
        amount: parseCheckoutPrice(checkout.total_tax),
        currencyCode:
          checkout.presentment_currency ?? checkout.currency ?? null,
      },
      shipping: shippingLine
        ? {
            amount: parseCheckoutPrice(shippingLine.price),
            currencyCode:
              checkout.presentment_currency ?? checkout.currency ?? null,
          }
        : null,
      totalPrice: {
        amount: parseCheckoutPrice(checkout.total_price),
        currencyCode:
          checkout.presentment_currency ?? checkout.currency ?? null,
      },
      shippingAddress: checkout.shipping_address
        ? shopifyWebhookAddressToRedoAddress(checkout.shipping_address)
        : null,
      billingAddress: checkout.billing_address
        ? shopifyWebhookAddressToRedoAddress(checkout.billing_address)
        : null,
      // Prefer pixel discountApplications if available (richer data), fallback to webhook discount_codes
      discountApplications:
        pixelCheckout?.discountApplications?.map(
          shopifyDiscountApplicationToRedoDiscount,
        ) ??
        shopifyWebhookDiscountCodesToRedoDiscounts(
          checkout.discount_codes,
          checkout.presentment_currency ?? checkout.currency,
        ),
      checkoutNoteAttributeTokens: buildCheckoutMetadataTokens(
        webhookNoteAttributesToMetadataEntries(checkout),
      ),
      checkoutLineItemPropertyTokens: buildCheckoutMetadataTokens(
        webhookLineItemPropertiesToMetadataEntries(checkout),
      ),
      shopifyCartDiscountAllocations,
    };
  }

  return {
    cartId,
    cartLines:
      checkout.lineItems?.map((lineItem) =>
        shopifyCheckoutLineItemToRedoCartLine(lineItem, redoCoverageProductIds),
      ) ?? null,
    subtotal: checkout.subtotalPrice
      ? {
          amount: checkout.subtotalPrice.amount,
          currencyCode: checkout.subtotalPrice.currencyCode,
        }
      : null,
    tax: checkout.totalTax
      ? {
          amount: checkout.totalTax.amount,
          currencyCode: checkout.totalTax.currencyCode,
        }
      : null,
    shipping: checkout.shippingLine
      ? {
          amount: checkout.shippingLine.price.amount ?? null,
          currencyCode: checkout.shippingLine.price.currencyCode ?? null,
        }
      : null,
    totalPrice: checkout.totalPrice
      ? {
          amount: checkout.totalPrice.amount,
          currencyCode: checkout.totalPrice.currencyCode,
        }
      : null,
    shippingAddress: checkout.shippingAddress
      ? shopifyMailingAddressToRedoAddress(checkout.shippingAddress)
      : null,
    billingAddress: checkout.billingAddress
      ? shopifyMailingAddressToRedoAddress(checkout.billingAddress)
      : null,
    discountApplications:
      checkout.discountApplications?.map(
        shopifyDiscountApplicationToRedoDiscount,
      ) ?? null,
    checkoutNoteAttributeTokens: buildCheckoutMetadataTokens(
      pixelCheckoutAttributesToMetadataEntries(checkout),
    ),
    checkoutLineItemPropertyTokens: buildCheckoutMetadataTokens(
      pixelLineItemPropertiesToMetadataEntries(checkout),
    ),
    shopifyCartDiscountAllocations,
  };
}

///////////////////////////
//// CommentSold types ////
///////////////////////////

/**
 * Convert CommentSold LineItem to Redo cart line format
 * Note: CommentSold sends prices in pennies, so we divide by 100
 */
export function commentSoldLineItemToRedoCartLine(
  lineItem: {
    variantId: number;
    productId: number;
    name: string;
    sku: string | null;
    mainImageUrl: string | null;
    attr1Name: string;
    attr2Name: string;
    attr1: string;
    attr2: string;
    price: { amount: number; currencyCode: string };
    originalPrice: { amount: number; currencyCode: string };
  },
  quantity: number,
): z.infer<typeof redoCartLineSchema> {
  return {
    parentProduct: {
      productId: String(lineItem.productId),
      title: lineItem.name,
      untranslatedTitle: lineItem.name,
      url: null,
    },
    isRedoProduct: false, // TODO: when would this be true?
    variantId: String(lineItem.variantId),
    sku: lineItem.sku,
    imageUrl: lineItem.mainImageUrl,
    price: {
      amount: lineItem.price.amount / 100,
      currencyCode: lineItem.price.currencyCode,
    },
    quantity,
  };
}

/**
 * Convert CommentSold product variant to Redo product format
 * Note: CommentSold sends prices in pennies, so we divide by 100
 */
export function commentSoldProductVariantToRedoProduct({
  variant,
  product,
  url,
}: {
  variant?: {
    id: number;
    attr1: string;
    attr2: string;
    price: { amount: number; currencyCode: string };
    originalPrice: { amount: number; currencyCode: string };
    availableQuantity: number;
  };
  product: {
    id: number;
    name: string;
    sku: string | null;
    mainImageUrl: string | null;
    attr1Name: string;
    attr2Name: string;
  };
  url: string;
}): z.infer<typeof redoStorefrontEventProductSchema> {
  return {
    parentProduct: {
      productId: String(product.id),
      title: product.name,
      untranslatedTitle: product.name,
      url,
    },
    isRedoProduct: false,
    variantId: variant?.id ? String(variant.id) : null,
    sku: product.sku,
    imageUrl: product.mainImageUrl,
    price: variant
      ? {
          amount: variant.price.amount / 100,
          currencyCode: variant.price.currencyCode,
        }
      : null,
  };
}
